import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import ScrollToTopButton from "../components/ScrollToTopButton";
import HeroPosterCard from "../components/HeroPosterCard";
import {
  CATEGORY_ORDER,
  MANIFEST_CATEGORIES,
  APPENDIX_CATEGORY,
  categoryToSlug,
  slugToCategory,
} from "../data/publications";
import { getIssue } from "../api/issuesApi";
import { API_ORIGIN } from "../api/authApi";

// Issue number 12 (see backend domain.ManifestCategories/AppendixCategory)
// browses differently from every other issue: instead of the six
// ValidCategories straight away, it shows two groups first — "درباره
// مانیفست" (which then reveals the standard six) and "مانیفست‌ها" (which
// reveals MANIFEST_CATEGORIES instead) — plus a third, flat "پیوست" entry
// with no sub-categories of its own. Every other issue skips this extra
// level entirely.
const GROUPS = {
  manifestoes: { label: "درباره مانیفست", categories: CATEGORY_ORDER },
  aboutManifesto: { label: "مانیفست‌ها", categories: MANIFEST_CATEGORIES },
};

// Every category name across both groups plus the flat APPENDIX_CATEGORY
// is globally unique, so a category name alone is enough to recover
// which group (if any) it lives under — lets a shared "?category=" link
// restore the exact same view without also having to encode the group.
function resolveGroupForCategory(category) {
  for (const [group, { categories }] of Object.entries(GROUPS)) {
    if (categories.includes(category)) return group;
  }
  return null;
}

// Section A: public. One page per issue (/issues/:id). The first
// category's cards are shown by default; clicking a category button
// switches to just that category's cards. Clicking the active category
// again hides them. Mirrors PublicationDetailPage.js.
//
// The selected category is mirrored into the "?category=" query string
// (see updateCategoryParam) so every category — including, for issue 12,
// a group's sub-category or the flat پیوست entry — has its own
// shareable/bookmarkable URL, restored on load below.
function IssueDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const isManifestIssue = issue?.number === 12;

  useEffect(() => {
    getIssue(id)
      .then((iss) => {
        const topicsByCategory = {};
        for (const t of iss.topics) {
          (topicsByCategory[t.category] ??= []).push(t);
        }
        setIssue({ ...iss, topics: topicsByCategory });

        const categoryFromUrl = slugToCategory(searchParams.get("category"));
        if (iss.number === 12) {
          const group = resolveGroupForCategory(categoryFromUrl);
          if (group) {
            setSelectedGroup(group);
            setSelectedCategory(categoryFromUrl);
          } else if (categoryFromUrl === APPENDIX_CATEGORY) {
            setSelectedGroup(null);
            setSelectedCategory(APPENDIX_CATEGORY);
          } else {
            // Every other issue starts with its first category already
            // expanded; issue 12 starts collapsed since it has an extra
            // group level to pick first.
            setSelectedGroup(null);
            setSelectedCategory(null);
          }
        } else {
          setSelectedCategory(CATEGORY_ORDER.includes(categoryFromUrl) ? categoryFromUrl : CATEGORY_ORDER[0]);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // Only re-resolved when navigating to a different issue id — not
    // meant to react to our own setSearchParams calls below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>در حال بارگذاری...</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !issue) {
    return (
      <div className="site">
        <SiteHeader />
        <main className="pub-not-found">
          <p>این شماره یافت نشد.</p>
          <Link to="/publications" className="back-link">
            ← بازگشت به نشریه‌ها
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const revealedCards = selectedCategory ? issue.topics[selectedCategory] || [] : [];
  const activeCategories = isManifestIssue
    ? (selectedGroup ? GROUPS[selectedGroup].categories : [])
    : CATEGORY_ORDER;

  function updateCategoryParam(category) {
    const params = new URLSearchParams(searchParams);
    if (category) {
      params.set("category", categoryToSlug(category));
    } else {
      params.delete("category");
    }
    setSearchParams(params, { replace: true });
  }

  function handleCategoryClick(category) {
    const next = selectedCategory === category ? null : category;
    setSelectedCategory(next);
    updateCategoryParam(next);
  }

  function handleGroupClick(group) {
    setSelectedGroup((current) => (current === group ? null : group));
    setSelectedCategory(null);
    updateCategoryParam(null);
  }

  // پیوست has no sub-categories of its own — selecting it goes straight
  // to its cards, bypassing the group/sub-category tier entirely.
  function handleAppendixClick() {
    const next = selectedCategory === APPENDIX_CATEGORY ? null : APPENDIX_CATEGORY;
    setSelectedGroup(null);
    setSelectedCategory(next);
    updateCategoryParam(next);
  }

  return (
    <div className="site">
      <SiteHeader />

      <main>
        <div className="dimension-divider">
          <span className="dimension-line" />
          <span className="dimension-label">{isManifestIssue ? "سه بخش" : "شش دسته"}</span>
          <span className="dimension-line" />
        </div>

        <section className="pub-topics-section">
          {isManifestIssue && (
            <div className="pub-category-buttons">
              {Object.entries(GROUPS).map(([group, { label }]) => (
                <button
                  key={group}
                  type="button"
                  className={
                    "pub-category-button" + (selectedGroup === group ? " pub-category-button--active" : "")
                  }
                  onClick={() => handleGroupClick(group)}
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                className={
                  "pub-category-button" +
                  (selectedCategory === APPENDIX_CATEGORY ? " pub-category-button--active" : "")
                }
                onClick={handleAppendixClick}
              >
                {APPENDIX_CATEGORY}
              </button>
            </div>
          )}

          {(!isManifestIssue || selectedGroup) && (
            <div className="pub-category-buttons">
              {activeCategories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={
                    "pub-category-button" +
                    (selectedCategory === category ? " pub-category-button--active" : "")
                  }
                  onClick={() => handleCategoryClick(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {selectedCategory ? (
            <>
              <p className="pub-topics-hint">
                موضوعات «{selectedCategory}» در شمارهٔ {issue.title}
              </p>
              <ul className="pub-topic-grid">
                {revealedCards.map((topic, index) => (
                  <li
                    className="pub-topic-card--emerge"
                    style={{ animationDelay: `${index * 70}ms` }}
                    key={topic.id}
                  >
                    <Link to={`/issues/${id}/topics/${topic.id}`} className="pub-topic-card-link">
                      <HeroPosterCard
                        titleTag="h3"
                        kicker={topic.category}
                        title={topic.title_fa || topic.title_en}
                        byline={topic.byline_fa || topic.byline_en}
                        subtitle={topic.description_fa || topic.description_en}
                        imageSrc={topic.image_path ? `${API_ORIGIN}${topic.image_path}` : undefined}
                        imageAlt={topic.title_fa || topic.title_en}
                        showRule
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="pub-topics-hint">
              برای دیدن موضوعات این شماره، یکی از دسته‌های بالا را انتخاب کنید.
            </p>
          )}
        </section>
      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}

export default IssueDetailPage;
